import { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../../constants/common'
import { SpeechToTextStatus } from '../../../../constants/speechToText'
import { useSpeechToText } from '../../../../hooks'
import {
  AI_PROMPT_MAX_LENGTH,
  AI_TEXT_GENERATION_VOICE_PLACEHOLDER_KEYS,
  AI_VOICE_INPUT_ERRORS,
  AI_VOICE_INPUT_HINT_PLACEHOLDER_STEPS,
  AI_VOICE_INPUT_HINT_TIMINGS_MS,
  AI_VOICE_INPUT_RECORDING_SILENCE_TIMEOUT_MS,
} from '../../AITextGeneration.constants'
import { getAITextGenerationTranscriptBudget } from '../../AITextGeneration.utils'
import {
  AIVoiceInputState,
  isAIVoiceInputStateApplying,
  isAIVoiceInputStateConnecting,
  isAIVoiceInputStateDefault,
  isAIVoiceInputStateReady,
  isAIVoiceInputStateRecording,
} from '../../AIVoiceInput'

import {
  appendTranscript,
  mapSpeechToTextErrorToAiVoiceError,
} from './useAIVoiceInputState.utils'

export function useAIVoiceInputState({
  getCurrentPromptValue = EMPTY_FUNCTION,
  onDisplayValueChange = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const { t } = useTranslation()

  const [state, setState] = useState(AIVoiceInputState.Default)
  const [placeholderKey, setPlaceholderKey] = useState(null)
  const [errorKey, setErrorKey] = useState(null)

  const callbacksRef = useRef({ getCurrentPromptValue, onDisplayValueChange })
  const stateRef = useRef(state)
  const isMountedRef = useRef(true)

  const snapshotRef = useRef(EMPTY_STRING)
  const finalizedRef = useRef(EMPTY_STRING)
  const interimRef = useRef(EMPTY_STRING)
  const transcriptBudgetRef = useRef(AI_PROMPT_MAX_LENGTH)
  const hintTimerIdsRef = useRef(EMPTY_LIST)
  const silenceTimerIdRef = useRef(null)
  // Holds the latest shared-hook handle so timer callbacks can reach stop/cancel
  // without re-binding their useCallbacks on every render.
  const speechToTextRef = useRef(null)

  // No deps on purpose: refresh on every render so async SDK callbacks always
  // see the latest props without forcing useCallback identities to change.
  useEffect(() => {
    callbacksRef.current = { getCurrentPromptValue, onDisplayValueChange }
  })

  useEffect(() => {
    stateRef.current = state
  }, [state])

  function clearHintTimers() {
    hintTimerIdsRef.current.forEach(clearTimeout)
    hintTimerIdsRef.current = EMPTY_LIST
  }

  function clearSilenceTimer() {
    if (silenceTimerIdRef.current !== null) {
      clearTimeout(silenceTimerIdRef.current)
      silenceTimerIdRef.current = null
    }
  }

  function resetSessionState() {
    finalizedRef.current = EMPTY_STRING
    interimRef.current = EMPTY_STRING
  }

  function writeLiveDisplay() {
    const finalized = finalizedRef.current
    const interim = interimRef.current
    const live =
      finalized && interim
        ? `${finalized} ${interim}`
        : finalized || interim || EMPTY_STRING

    // Render the live transcript as a continuation of the previously typed
    // text so the original prompt stays visible during recording. Hard-clamp
    // to the max length: the budget reserves room for the snapshot + joiner,
    // but the space inserted between finalized and interim can push the live
    // display one char past the limit, and programmatic value updates bypass
    // the textarea's maxLength.
    const display = appendTranscript(snapshotRef.current, live).slice(
      0,
      AI_PROMPT_MAX_LENGTH
    )

    callbacksRef.current.onDisplayValueChange(display)
  }

  function writeApplied() {
    const committed = appendTranscript(
      snapshotRef.current,
      finalizedRef.current
    )

    callbacksRef.current.onDisplayValueChange(committed)
  }

  function writeSnapshot() {
    callbacksRef.current.onDisplayValueChange(snapshotRef.current)
  }

  function scheduleSilenceTimer() {
    clearSilenceTimer()
    silenceTimerIdRef.current = setTimeout(() => {
      if (isAIVoiceInputStateRecording(stateRef.current)) {
        speechToTextRef.current?.stop()
      }
    }, AI_VOICE_INPUT_RECORDING_SILENCE_TIMEOUT_MS)
  }

  function scheduleHintTimers() {
    clearHintTimers()

    const placeholderTimers = AI_VOICE_INPUT_HINT_PLACEHOLDER_STEPS.map(
      ({ delay, placeholderKey: key }) =>
        setTimeout(() => {
          if (isAIVoiceInputStateReady(stateRef.current)) {
            setPlaceholderKey(key)
          }
        }, delay)
    )

    const autoCloseTimer = setTimeout(() => {
      if (isAIVoiceInputStateReady(stateRef.current)) {
        speechToTextRef.current?.cancel()
      }
    }, AI_VOICE_INPUT_HINT_TIMINGS_MS.AutoClose)

    hintTimerIdsRef.current = [...placeholderTimers, autoCloseTimer]
  }

  function handleStatusChange(nextStatus) {
    if (isMountedRef.current) {
      switch (nextStatus) {
        case SpeechToTextStatus.Connecting: {
          // Button shows loading; prompt + placeholder stay untouched until
          // onMicrophoneReady confirms mic was actually granted.
          setState(AIVoiceInputState.Connecting)
          break
        }

        case SpeechToTextStatus.Ready: {
          setState(AIVoiceInputState.Ready)
          setPlaceholderKey(AI_TEXT_GENERATION_VOICE_PLACEHOLDER_KEYS.Ready)
          scheduleHintTimers()
          break
        }

        case SpeechToTextStatus.Recording: {
          clearHintTimers()
          setPlaceholderKey(null)
          setState(AIVoiceInputState.Recording)
          break
        }

        case SpeechToTextStatus.Stopping: {
          clearHintTimers()
          clearSilenceTimer()
          setPlaceholderKey(null)
          setState(AIVoiceInputState.Applying)
          break
        }

        case SpeechToTextStatus.Idle: {
          clearHintTimers()
          clearSilenceTimer()
          const previousState = stateRef.current

          if (isAIVoiceInputStateApplying(previousState)) {
            writeApplied()
          } else if (
            isAIVoiceInputStateConnecting(previousState) ||
            isAIVoiceInputStateReady(previousState) ||
            isAIVoiceInputStateRecording(previousState)
          ) {
            writeSnapshot()
          }

          resetSessionState()
          setPlaceholderKey(null)
          setState(AIVoiceInputState.Default)
          break
        }
        default:
          break
      }
    }
  }

  function handleMicrophoneReady() {
    if (isMountedRef.current) {
      // Mic access confirmed — re-commit the snapshot so previously typed text
      // stays visible during recording, then reveal the Connecting placeholder.
      // flushSync forces the value to commit before the placeholder render, so
      // TextareaElement's empty-value check mounts the placeholder only when
      // there was no prior text.
      flushSync(() => {
        callbacksRef.current.onDisplayValueChange(snapshotRef.current)
      })
      setPlaceholderKey(AI_TEXT_GENERATION_VOICE_PLACEHOLDER_KEYS.Connecting)
    }
  }

  function handleInterimTranscript(interim) {
    const remainingBudget = Math.max(
      0,
      transcriptBudgetRef.current - finalizedRef.current.length
    )
    const safeInterim = interim || EMPTY_STRING
    const isBudgetExhausted =
      remainingBudget === 0 || safeInterim.length >= remainingBudget
    const clampedInterim =
      safeInterim.length > remainingBudget
        ? safeInterim.slice(0, remainingBudget)
        : safeInterim

    interimRef.current = clampedInterim

    writeLiveDisplay()

    if (isBudgetExhausted) {
      speechToTextRef.current?.stop()

      return
    }

    if (
      clampedInterim &&
      (isAIVoiceInputStateReady(stateRef.current) ||
        isAIVoiceInputStateRecording(stateRef.current))
    ) {
      scheduleSilenceTimer()
    }
  }

  function handleFinalTranscript(finalized) {
    const budget = transcriptBudgetRef.current
    const isBudgetExceeded =
      budget > 0 && finalized.length >= budget && finalized.length > 0

    if (isBudgetExceeded) {
      finalizedRef.current = finalized.slice(0, budget)
      interimRef.current = EMPTY_STRING
      writeLiveDisplay()
      speechToTextRef.current?.stop()

      return
    }

    finalizedRef.current = finalized
    writeLiveDisplay()

    if (
      isAIVoiceInputStateReady(stateRef.current) ||
      isAIVoiceInputStateRecording(stateRef.current)
    ) {
      scheduleSilenceTimer()
    }
  }

  function handleError(errorCode) {
    if (isMountedRef.current) {
      writeSnapshot()
      setErrorKey(mapSpeechToTextErrorToAiVoiceError(errorCode))
    }
  }

  const speechToText = useSpeechToText({
    onError: handleError,
    onFinalTranscript: handleFinalTranscript,
    onInterimTranscript: handleInterimTranscript,
    onMicrophoneReady: handleMicrophoneReady,
    onStatusChange: handleStatusChange,
  })

  // No deps on purpose: refresh on every render so timer callbacks reach the
  // latest speechToText handle without re-creating their useCallbacks.
  useEffect(() => {
    speechToTextRef.current = speechToText
  })

  function start() {
    if (isAIVoiceInputStateDefault(stateRef.current)) {
      const snapshot =
        callbacksRef.current.getCurrentPromptValue() ?? EMPTY_STRING
      const budget = getAITextGenerationTranscriptBudget(
        snapshot,
        AI_PROMPT_MAX_LENGTH
      )

      if (budget > 0) {
        setErrorKey(null)
        snapshotRef.current = snapshot
        transcriptBudgetRef.current = budget
        resetSessionState()

        speechToTextRef.current?.start()
      }
    }
  }

  function stop() {
    const currentState = stateRef.current

    if (
      isAIVoiceInputStateReady(currentState) ||
      isAIVoiceInputStateRecording(currentState)
    ) {
      speechToTextRef.current?.stop()
    } else if (isAIVoiceInputStateConnecting(currentState)) {
      speechToTextRef.current?.cancel()
    }
  }

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      clearHintTimers()
      clearSilenceTimer()
    }
  }, [])

  return {
    errorMessage: errorKey ? t(AI_VOICE_INPUT_ERRORS[errorKey]) : null,
    isActive: !isAIVoiceInputStateDefault(state),
    placeholderKey,
    start,
    state,
    stop,
  }
}
